<template>
  <div class="outsourcing-pricing-page">
    <b-card class="pricing-card">
      <div class="pricing-header">
        <div>
          <h2 class="pricing-title">
            Tarifs sous-traitance
          </h2>
          <p class="pricing-subtitle">
            Configurez le prix de livraison et le prix de retour par société de sous-traitance.
          </p>
        </div>
        <b-button
          variant="outline-primary"
          :disabled="loading"
          @click="fetchCompanies"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            class="mr-50"
          />
          Actualiser
        </b-button>
      </div>

      <b-alert
        v-if="error"
        show
        variant="danger"
        class="mt-2"
      >
        {{ error }}
      </b-alert>

      <div class="toolbar">
        <b-form-input
          v-model="search"
          placeholder="Rechercher une société..."
          class="search-input"
        />
        <div class="count-label">
          {{ filteredCompanies.length }} société{{ filteredCompanies.length > 1 ? 's' : '' }}
        </div>
      </div>

      <b-overlay
        :show="loading"
        rounded="sm"
      >
        <b-table
          :items="filteredCompanies"
          :fields="fields"
          responsive
          hover
          show-empty
          empty-text="Aucune société de sous-traitance trouvée"
          class="pricing-table"
        >
          <template #cell(company_label)="{ item }">
            <div class="company-cell">
              <strong>{{ item.company_label || '-' }}</strong>
              <small
                v-if="item.name && item.name !== item.company_label"
                class="text-muted"
              >
                {{ item.name }}
              </small>
            </div>
          </template>

          <template #cell(price_delivery)="{ item }">
            <div class="price-editor">
              <input
                v-model.number="item.price_delivery"
                type="number"
                min="0"
                step="0.001"
                placeholder="0.000"
                class="price-input"
                :disabled="item.saving"
              >
              <span class="price-currency">DT</span>
            </div>
          </template>

          <template #cell(price_return)="{ item }">
            <div class="price-editor">
              <input
                v-model.number="item.price_return"
                type="number"
                min="0"
                step="0.001"
                placeholder="0.000"
                class="price-input"
                :disabled="item.saving"
              >
              <span class="price-currency">DT</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="actions">
              <b-button
                variant="primary"
                size="sm"
                :disabled="item.saving || !isDirty(item)"
                @click="saveCompany(item)"
              >
                <b-spinner
                  v-if="item.saving"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  size="14"
                  class="mr-50"
                />
                Enregistrer
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                :disabled="item.saving || !isDirty(item)"
                @click="resetCompany(item)"
              >
                Annuler
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'OutsourcingPricing',
  data() {
    return {
      loading: false,
      error: '',
      search: '',
      companies: [],
      fields: [
        { key: 'company_label', label: 'Société de sous-traitance' },
        { key: 'price_delivery', label: 'Prix livraison' },
        { key: 'price_return', label: 'Prix retour' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    filteredCompanies() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.companies
      return this.companies.filter(company => {
        const label = `${company.company_label || ''} ${company.name || ''}`.toLowerCase()
        return label.includes(query)
      })
    },
  },
  created() {
    this.fetchCompanies()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    normalizePrice(value) {
      const numeric = Number(value)
      return Number.isFinite(numeric) ? numeric : 0
    },
    normalizeCompany(company) {
      const priceDelivery = this.normalizePrice(company.price_delivery)
      const priceReturn = this.normalizePrice(company.price_return)
      return {
        ...company,
        price_delivery: priceDelivery,
        price_return: priceReturn,
        original_price_delivery: priceDelivery,
        original_price_return: priceReturn,
        saving: false,
      }
    },
    async fetchCompanies() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get('/api/companies/outsourcing-company/pricing/')
        const rows = Array.isArray(data) ? data : data.results || []
        this.companies = rows.map(this.normalizeCompany)
      } catch (error) {
        this.error = 'Impossible de charger les tarifs sous-traitance.'
      } finally {
        this.loading = false
      }
    },
    isDirty(company) {
      return (
        this.normalizePrice(company.price_delivery) !== this.normalizePrice(company.original_price_delivery)
        || this.normalizePrice(company.price_return) !== this.normalizePrice(company.original_price_return)
      )
    },
    findCompany(company) {
      return this.companies.find(row => row.id === company.id)
    },
    resetCompany(company) {
      const target = this.findCompany(company)
      if (!target) return
      target.price_delivery = target.original_price_delivery
      target.price_return = target.original_price_return
    },
    extractErrorMessage(error) {
      const data = error.response && error.response.data
      if (!data) return 'Impossible d’enregistrer les tarifs.'
      if (typeof data === 'string') return data
      if (data.detail) return data.detail
      const firstKey = Object.keys(data)[0]
      if (!firstKey) return 'Impossible d’enregistrer les tarifs.'
      const value = data[firstKey]
      if (Array.isArray(value)) return `${firstKey}: ${value.join(', ')}`
      return `${firstKey}: ${value}`
    },
    async saveCompany(company) {
      const target = this.findCompany(company)
      if (!target) return
      const priceDelivery = this.normalizePrice(target.price_delivery)
      const priceReturn = this.normalizePrice(target.price_return)
      if (priceDelivery < 0 || priceReturn < 0) {
        this.$bvToast.toast('Les prix ne peuvent pas être négatifs.', {
          title: 'Validation',
          variant: 'danger',
          solid: true,
        })
        return
      }

      target.saving = true
      try {
        const { data } = await axios.patch(`/api/companies/outsourcing-company/pricing/${target.id}/`, {
          price_delivery: priceDelivery,
          price_return: priceReturn,
        })
        Object.assign(target, this.normalizeCompany(data))
        this.$bvToast.toast('Tarifs enregistrés avec succès.', {
          title: 'Tarifs sous-traitance',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast(this.extractErrorMessage(error), {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        target.saving = false
      }
    },
  },
}
</script>

<style scoped>
.outsourcing-pricing-page {
  padding-bottom: 2rem;
}

.pricing-card {
  border-radius: 8px;
}

.pricing-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.pricing-title {
  margin-bottom: 0.35rem;
  font-size: 1.5rem;
  font-weight: 700;
}

.pricing-subtitle {
  margin-bottom: 0;
  color: #6e6b7b;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0 1rem;
}

.search-input {
  max-width: 360px;
}

.count-label {
  color: #6e6b7b;
  font-weight: 600;
}

.company-cell {
  display: flex;
  flex-direction: column;
  min-width: 180px;
}

.pricing-table {
  margin-bottom: 0;
}

.price-editor {
  display: inline-flex;
  align-items: stretch;
  width: 150px;
  min-width: 150px;
}

.price-input {
  box-sizing: border-box;
  display: block;
  width: 104px !important;
  min-width: 104px !important;
  height: 2.714rem;
  flex: 0 0 104px;
  padding: 0.438rem 0.75rem;
  color: #6e6b7b;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  outline: none;
  text-align: right;
}

.price-input:focus {
  border-color: #d00000;
  box-shadow: 0 3px 10px 0 rgba(208, 0, 0, 0.1);
}

.price-input:disabled {
  background-color: #efefef;
}

.price-currency {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  min-width: 46px;
  padding: 0 0.75rem;
  color: #5e5873;
  background: #fff;
  border: 1px solid #d8d6de;
  border-left: 0;
  border-top-right-radius: 0.357rem;
  border-bottom-right-radius: 0.357rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .pricing-header,
  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .search-input {
    max-width: none;
  }
}
</style>
